/**
 * Shared visual tokens for the "editable list row / form panel" surfaces used across
 * Clients (Manage Clients) and Account (Account Settings, Assessment Options, Manage
 * Team, Manage Accounts) views.
 *
 * Theme intent: a quiet, flat UI that draws the eye to one primary action per view
 * while everything else — labels, values, controls, secondary buttons — stays
 * low-contrast and out of the way. White panels, no borders except separators between
 * sections/rows, light-gray placeholders and secondary buttons.
 *
 * Sampled from the patterns worked out in ProfileRowEditor.tsx; extend this file
 * (not ad-hoc hex literals) as new editor UI is built anywhere in the app.
 */

export const EDITOR_COLORS = {
  background: '#ffffff',
  separator: '#e5ebf3',
  controlBorder: '#cbd5e1',
  textPrimary: '#0f172a',
  textSecondary: '#334155',
  textMuted: '#64748b',
  placeholder: '#94a3b8',
  accent: '#2563eb',
  action: '#16a34a',
  actionText: '#ffffff',
  submitBackground: '#d5dbe6',
  submitBackgroundDisabled: '#e5e7eb',
  submitText: '#111827',
  errorBackground: '#fef2f2',
  errorText: '#b91c1c',
  successText: '#15803d',
} as const;

export const EDITOR_FONT_FAMILY = 'Arial';
export const EDITOR_FONT_SIZE = 13;
export const EDITOR_FONT_WEIGHT = '400' as const;

/** Base text style to spread into RN StyleSheet entries: `{ ...editorText, color: ... }` */
export const editorText = {
  fontFamily: EDITOR_FONT_FAMILY,
  fontSize: EDITOR_FONT_SIZE,
  fontWeight: EDITOR_FONT_WEIGHT,
};

/** Shared inline style for native HTML form controls rendered on web (select/date). */
export const webControlStyle = {
  height: 26,
  border: `1px solid ${EDITOR_COLORS.controlBorder}`,
  borderRadius: 3,
  padding: '0 7px',
  fontFamily: EDITOR_FONT_FAMILY,
  fontSize: EDITOR_FONT_SIZE,
  color: EDITOR_COLORS.textPrimary,
  backgroundColor: EDITOR_COLORS.background,
  boxSizing: 'border-box',
} as const;

/** Shared inline style for a native HTML checkbox rendered on web. */
export const webCheckboxStyle = {
  width: 16,
  height: 16,
  accentColor: EDITOR_COLORS.accent,
  cursor: 'pointer',
} as const;
